import { useParams, Link } from "react-router-dom";
import { getArticleBySlug, getRelatedArticles } from "@/mocks/articles";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";
import ScrollToTop from "@/components/feature/ScrollToTop";
import NotFound from "@/pages/NotFound";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return <NotFound />;
  }

  const relatedArticles = getRelatedArticles(article.slug);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    author: {
      "@type": "Person",
      name: article.author,
    },
    datePublished: article.publishDate,
    image: article.coverImage,
    publisher: {
      "@type": "Organization",
      name: "宅男视频",
      url: "https://zhainanvideo.com",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://zhainanvideo.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 宅男视频`}
        description={article.excerpt}
        keywords={`${article.tags.join(",")},宅男视频,数字视频平台,免费高清`}
        canonicalUrl={`https://zhainanvideo.com/article/${article.slug}`}
        ogType="article"
        ogImage={article.coverImage}
        ogImageAlt={article.title}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <article className="w-full max-w-4xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: "首页", href: "/" },
                  { label: "文章资讯", href: "/" },
                  { label: article.title },
                ]}
              />
            </div>

            {/* Article Header */}
            <header className="mb-8">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {article.views} 阅读
                </span>
              </div>
            </header>

            {/* Cover Image */}
            <div className="aspect-[2/1] rounded-xl overflow-hidden mb-8 bg-background-200">
              <img
                src={article.coverImage}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-8">
              {article.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full border border-background-200/70"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Article Content */}
            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="mt-14 pt-10 border-t border-background-200/70">
                <h2 className="text-xl font-bold text-foreground-950 mb-6">相关文章</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.slug}`}
                      className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
                    >
                      <div className="aspect-[2/1] overflow-hidden">
                        <img
                          src={rel.coverImage}
                          alt={rel.title}
                          title={rel.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {rel.title}
                        </h3>
                        <p className="text-foreground-500 text-xs mt-2">{rel.readTime} · {rel.author}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}